import Button from '@ramsey-design-system/button';
import React from 'react';
import ical, {
  ICalAlarmType,
  ICalCalendar,
  ICalEventBusyStatus,
} from 'ical-generator';
import styles from './add-to-calendar.module.css';
import { downloadBlob, getAlarmType, getBusyStatus } from './lib/utils';
import { FpuCalendarCopy, AddToCalendarEvent } from './FpuCalendarCopy';
import Heading from '@ramsey-design-system/heading';
import RichText from '@ramsey-design-system/rich-text';
import { AddToCalendarProps, CustomAddToCalendarEvent } from './types';

const createSingleFPUEvent = (
  calendar: ICalCalendar,
  start: Date,
  calEvent: AddToCalendarEvent
) => {
  // Each event is one hour long
  const end = new Date(start.getTime());
  end.setHours(start.getHours() + 1);

  const { summary, description, url } = calEvent;

  const event = calendar.createEvent({
    start,
    end,
    summary,
    description,
    url,
    busystatus: getBusyStatus(),
  });

  event.createAlarm({
    type: getAlarmType(),
    trigger: 1800,
  });
};

const createCustomCalendarEvents = (
  calendar: ICalCalendar,
  customEvents: CustomAddToCalendarEvent[]
) => {
  customEvents.forEach(({ start, end, summary, description, url }) => {
    const event = calendar.createEvent({
      start,
      end,
      summary,
      description,
      url,
      busystatus: ICalEventBusyStatus.BUSY,
    });

    event.createAlarm({
      type: ICalAlarmType.audio,
      trigger: 1800,
    });
  });
};

const onClick = (
  now: Date,
  customEvents: CustomAddToCalendarEvent[] | undefined,
  calendarName: string,
  fileName: string,
  defaultUrl: string
) => {
  const calendar = ical({
    name: calendarName,
  });

  if (customEvents) {
    createCustomCalendarEvents(calendar, customEvents);
  } else {
    let currentDate = new Date(now.getTime());

    FpuCalendarCopy.forEach(calEvent => {
      calEvent.url = defaultUrl;
      createSingleFPUEvent(
        calendar,
        currentDate,
        calEvent as AddToCalendarEvent
      );
      currentDate = new Date(currentDate.getTime());
      // 1 week
      currentDate.setHours(currentDate.getHours() + 168);
    });
  }

  const blob = calendar.toBlob();
  downloadBlob(blob, fileName);
};

/**
 * Renders a button that adds to the user's calendar
 * Schedule for this hour, for ONE hour.
 * Repeat 9 times
 * Reminder: 30 mins
 *
 * Create your own set of events (or 1) by passing in `customEvents`
 *
 * @param param0
 * @returns
 */
const AddToCalendar = ({
  title = 'Schedule Your Success',
  description = "People who are most successful set aside time each week to watch the videos. Consistency is key! So, let's get it on the calendar.",
  buttonText = 'Add Calendar Reminders',
  startDate = new Date(),
  defaultUrl = 'https://ramsey.link/FPUCAL',
  calendarName = 'Financial Peace University Calendar',
  calendarFileName = 'fpu.ics',
  customEvents,
}: AddToCalendarProps) => {
  // Inline svg calendar icon for portability
  const icon = () => (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M11.6667 2.50033H14.7222C15.0597 2.50033 15.3333 2.77393 15.3333 3.11144V14.1114C15.3333 14.4489 15.0597 14.7225 14.7222 14.7225H1.27777C0.94026 14.7225 0.666656 14.4489 0.666656 14.1114V3.11144C0.666656 2.77393 0.94026 2.50033 1.27777 2.50033H4.33332V1.2781C4.33332 0.940596 4.60693 0.666992 4.94443 0.666992C5.28194 0.666992 5.55555 0.940596 5.55555 1.2781V2.50033H10.4444V1.2781C10.4444 0.940596 10.718 0.666992 11.0555 0.666992C11.3931 0.666992 11.6667 0.940596 11.6667 1.2781V2.50033ZM1.88888 13.5003H14.1111V4.94477H1.88888V13.5003ZM5.55555 6.7781H3.1111V8.61144H5.55555V6.7781ZM9.22221 6.7781H6.77777V8.61144H9.22221V6.7781ZM3.1111 9.83366H5.55555V11.667H3.1111V9.83366ZM9.22221 9.83366H6.77777V11.667H9.22221V9.83366ZM10.4444 6.7781H12.8889V8.61144H10.4444V6.7781Z"
        fill="#0073B9"
      />
    </svg>
  );

  // Default to FPU schedule

  return (
    <div className={styles.container}>
      <Heading className={styles.bottom_space} level="2" size="small">
        {title}
      </Heading>
      <RichText
        className={`${styles.bottom_space} ${styles.gray_color}`}
        size="small"
      >
        {description}
      </RichText>
      <Button
        className={styles.button}
        appearance="ghost"
        fullWidth={true}
        icon={icon}
        onClick={() =>
          onClick(
            startDate,
            customEvents,
            calendarName,
            calendarFileName,
            defaultUrl
          )
        }
        data-testid="add-to-calendar-button"
        data-logger-element-id={'Add To Calendar Button'}
        data-logger-element-label={'Add To Calendar'}
      >
        {buttonText}
      </Button>
    </div>
  );
};

export { AddToCalendar };
